// app/javascript/api/message.js

import { defaultServerErrorMessage, isProduction } from "../utils/constants";
import { bugsnag_log } from "../utils/bugsnag";
import request from "graphql-request";
import { getTabId } from "../utils/tabId";
import { buildAttachmentsPayload } from "../utils/images_uploads_attachments";

const graphqlEndpoint = () => {
  if (typeof window === "undefined") return "/graphql";

  try {
    if (!["http:", "https:"].includes(window.location.protocol)) return "/graphql";

    return new URL("/graphql", window.location.origin).toString();
  } catch (_error) {
    return "/graphql";
  }
};

const responseDebugMetadata = (response) => ({
  status: response?.status ?? null,
  ok: response?.ok ?? null,
  url: response?.url ?? null,
  redirected: response?.redirected ?? null,
  type: response?.type ?? null,
  contentType: response?.headers?.get?.("content-type") ?? null,
});

const RETRYABLE_CHAT_HTTP_STATUSES = new Set([408, 425, 429, 443, 500, 502, 503, 504]);

const isBrowserOffline = () =>
  typeof navigator !== "undefined" && navigator.onLine === false;

const chatTransportFailureMessage = () =>
  isBrowserOffline()
    ? "You're offline. Reconnect, then retry this message."
    : "We couldn't reach Incite reliably. Check your connection, then retry this message.";

const retryableChatSendFailure = (details = {}) => ({
  error: chatTransportFailureMessage(),
  retryable: true,
  retryableTransportError: true,
  skipBotMessage: true,
  ...details,
});

const graphqlErrorCode = (error) => error?.code || error?.extensions?.code;
const conversionGateFromGraphqlError = (error) =>
  error?.extensions?.conversion_gate ||
  error?.extensions?.conversionGate ||
  error?.conversion_gate ||
  error?.conversionGate ||
  null;

const deleteChatThread = async (threadUuid) => {
  const body = JSON.stringify({
    query: `mutation DeleteChatThread($threadUuid: String!) {
      deleteChatThread(threadUuid: $threadUuid) {
      status}
      }`,
    variables: { threadUuid },
  });

  try {
    const res = await fetch("/graphql", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
    });
    const json = await res.json();

    if (json.errors?.length) {
      throw new Error(json.errors[0].message || defaultServerErrorMessage);
    }

    return res.status;
  } catch (err) {
    if (!isProduction) console.error("renameThread error:", err);
    bugsnag_log(err);
    throw err; //
  }
};

const renameThread = async (title, threadUuid) => {
  const body = JSON.stringify({
    query: `
      mutation RenameChatThread($threadUuid: String!, $title: String!) {
        renameChatThread(threadUuid: $threadUuid, title: $title) {
          title
        }
      }`,
    variables: { threadUuid, title },
  });

  try {
    const res = await fetch("/graphql", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
    });

    if (!res.ok) {
      throw new Error(`HTTP ${res.status} while renaming thread`);
    }

    const json = await res.json();

    if (json.errors?.length) {
      throw new Error(json.errors[0].message || defaultServerErrorMessage);
    }

    const newTitle = json?.data?.renameChatThread?.title;
    if (!newTitle) {
      throw new Error(defaultServerErrorMessage);
    }

    return newTitle;
  } catch (err) {
    if (!isProduction) console.error("renameThread error:", err);
    bugsnag_log(err);
    throw err; // let confirmRename's catch handle UI reaction
  }
};

const adminCreateTargetedThread = async ({
  userId,
  message,
  title,
  createAdminCopy,
} = {}) => {
  const body = JSON.stringify({
    query: `
      mutation AdminCreateTargetedThread($userId: ID!, $message: String!, $title: String, $createAdminCopy: Boolean) {
        adminCreateTargetedThread(userId: $userId, message: $message, title: $title, createAdminCopy: $createAdminCopy) {
          id
          threadUuid
          title
          createdAt
          updatedAt
          isAdminTargeted
          pairedThreadId
          pairedUserId
          pairedUserUsername
        }
      }`,
    variables: { userId, message, title, createAdminCopy },
  });

  try {
    const res = await fetch("/graphql", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
    });
    const json = await res.json();

    if (json.errors?.length) {
      throw new Error(json.errors[0].message || defaultServerErrorMessage);
    }

    return json?.data?.adminCreateTargetedThread;
  } catch (err) {
    if (!isProduction) console.error("adminCreateTargetedThread error:", err);
    bugsnag_log(err);
    throw err;
  }
};

const getAdminChatModelCatalog = async () => {
  const body = JSON.stringify({
    query: `
      {
        admin {
          getChatModelCatalog
        }
      }`,
  });

  try {
    const res = await fetch("/graphql", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
    });
    const json = await res.json();

    if (json.errors?.length) {
      throw new Error(json.errors[0].message || defaultServerErrorMessage);
    }

    return json?.data?.admin?.getChatModelCatalog || null;
  } catch (err) {
    if (!isProduction) console.error("getAdminChatModelCatalog error:", err);
    bugsnag_log(err);
    throw err;
  }
};

const getAllUserThreads = async () => {
  /* 1 ─ build request --------------------------------------------------- */
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `
        {
          getAllUserThreads {
            id
            threadUuid
            title
            createdAt
            updatedAt
            lastActivityAt
            hasUnread
            isAdminTargeted
            pairedThreadId
            pairedUserId
            pairedUserUsername
           
          }
        }`,
    }),
  };

  try {
    const res = await fetch("/graphql", requestOptions);
    const json = await res.json();

    if (json.errors && json.errors.length) {
      const error = json.errors[0];

      if (error.code === "AUTH") {
        return { error: "Session Expired" };
      }

      if (isProduction) {
        bugsnag_log(error, {
          context: "getAllUserThreads",
          backendStack: error.extensions?.exception?.stacktrace,
        });
        return { error: defaultServerErrorMessage };
      }
      return { error: error.message };
    }

    const threads = (json.data?.getAllUserThreads ?? []).map((t) => ({
      ...t,

      messages: t.messages ?? [],
    }));

    return threads.reverse();
  } catch (e) {
    console.error("getAllUserThreads error", e);
    if (isProduction) {
      bugsnag_log(e, { context: "getAllUserThreads (fetch failed)" });
    }
    return { error: defaultServerErrorMessage };
  }
};

const getUserThreadsPage = async ({
  limit = 30,
  offset = 0,
  sort = "updated_desc",
} = {}) => {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `
        query GetThreads($limit: Int!, $offset: Int!, $sort: String!) {
          getAllUserThreads(limit: $limit, offset: $offset, sort: $sort) {
            id
            threadUuid
            title
            createdAt
            updatedAt
            lastActivityAt
            hasUnread
            isAdminTargeted
            pairedThreadId
            pairedUserId
            pairedUserUsername
          }
        }`,
      variables: { limit, offset, sort },
    }),
  };

  try {
    const res = await fetch("/graphql", requestOptions);
    const json = await res.json();

    if (json.errors?.length) {
      const error = json.errors[0];
      if (error.code === "AUTH") return { error: "Session Expired" };
      if (isProduction) {
        bugsnag_log(error, {
          context: "getUserThreadsPage",
          backendStack: error.extensions?.exception?.stacktrace,
        });
        return { error: defaultServerErrorMessage };
      }
      return { error: error.message };
    }

    // Server already returns newest first; no reverse() needed
    const threads = (json.data?.getAllUserThreads ?? []).map((t) => ({
      ...t,
      messages: t.messages ?? [],
    }));
    return threads;
  } catch (e) {
    console.error("getUserThreadsPage error", e);
    if (isProduction)
      bugsnag_log(e, { context: "getUserThreadsPage (fetch failed)" });
    return { error: defaultServerErrorMessage };
  }
};

const getThreadMessages = async ({
  threadUuid,
  limit = 4,
  beforeTs = null,
  beforeId = null,
  messageId = null,
  lightweight = false,
}) => {
  const messageFields = lightweight
    ? `
        id
        sender
        senderUsername
        message
        advancedFilteringMessage
        createdAt
        interactionMode
        presentationMode
        voiceTurnId
        voiceSessionUuid
        voiceSessionStatus
        voiceSessionStartedAt
        voiceSessionEndedAt
        tickerDescription
        answeredTickerIds
        answeredTickerMap {
          id
          ticker
        }
        ticker
        currentPrice
        currentPriceUpdatedAt
        currentPriceUpdatedAtIso
        closePrice
        priceChange1d
        pricePctChange1d
        pricePctChangeAh
        pricePctChange24hr
        assetType
        deviceType
        logoUrls
        label1
        values1 { x y }
        availableSecondaryKeys
        primaryLabel
        searchUsed
        searchSources {
          title
          url
        }
        followupButtonQuestions
        tierRecommendation
        refinedQueryObj
        sportsCardData
        agenticChartState
        marketAnalysisArtifacts
        responseExtractedChartEntities
        workspaceTurnContext
        tradingScale
        tickerPrices {
          date
          open
          high
          low
          close
          volume
        }
        attachments {
          id
          kind
          filename
          mime
          bytes
          width
          height
          url
          thumbUrl
          createdAt
        }
      `
    : `
        id
        sender
        senderUsername
        message
        advancedFilteringMessage
        createdAt
        interactionMode
        presentationMode
        voiceTurnId
        voiceSessionUuid
        voiceSessionStatus
        voiceSessionStartedAt
        voiceSessionEndedAt
        tickerDescription
        answeredTickerIds
        answeredTickerMap {
          id
          ticker
        }
        ticker
        currentPrice
        currentPriceUpdatedAt
        currentPriceUpdatedAtIso
        closePrice
        priceChange1d
        pricePctChange1d
        pricePctChangeAh
        pricePctChange24hr
        assetType
        deviceType
        logoUrls
        label1
        label2
        label3
        dates
        primaryLabel
        values1 { x y }
        values2 { x y }
        values3 { x y }
        searchUsed
        searchSources {
          title
          url
        }
        followupButtonQuestions
        tierRecommendation
        refinedQueryObj
        sportsCardData
        agenticChartState
        marketAnalysisArtifacts
        responseExtractedChartEntities
        workspaceTurnContext
        tradingScale
        tickerPrices {
          date
          open
          high
          low
          close
          volume
        }
        attachments {
          id
          kind
          filename
          mime
          bytes
          width
          height
          url
          thumbUrl
          createdAt
        }
      `;

  const body = JSON.stringify({
    query: `
    query ThreadMsgs($threadUuid: String!, $limit: Int, $beforeTs: ISO8601DateTime, $beforeId: ID, $messageId: ID) {
        chatThreadMessages(threadUuid: $threadUuid, limit: $limit, beforeTs: $beforeTs, beforeId: $beforeId, messageId: $messageId) {
        ${messageFields}
        }
      }`,
    variables: { threadUuid, limit, beforeTs, beforeId, messageId },
  });

  try {
    const res = await fetch("/graphql", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
    });
    const json = await res.json();

    if (json.errors?.length) throw new Error(json.errors[0].message);
    return json.data.chatThreadMessages;
  } catch (err) {
    if (!isProduction) console.error("getThreadMessages error:", err);
    bugsnag_log(err);
    throw err;
  }
};

const searchUserMessages = async ({ searchTerm, limit = 8 } = {}) => {
  const term = String(searchTerm || "").trim();
  if (term.length < 2) return [];

  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `
        query SearchUserMessages($term: String!, $limit: Int) {
          searchUserMessages(searchTerm: $term, limit: $limit) {
            id
            message
            createdAt
            threadUuid
            threadTitle
            threadIsAdminTargeted
            previousHumanMessageId
            matchType
          }
        }`,
      variables: { term, limit },
    }),
  };

  try {
    const res = await fetch("/graphql", requestOptions);
    const json = await res.json();

    if (json.errors?.length) {
      const error = json.errors[0];
      if (graphqlErrorCode(error) === "AUTH") return { error: "Session Expired" };
      if (isProduction) {
        bugsnag_log(error, {
          context: "searchUserMessages",
          backendStack: error.extensions?.exception?.stacktrace,
        });
      }
      return { error: error.message || defaultServerErrorMessage };
    }

    return json?.data?.searchUserMessages || [];
  } catch (error) {
    if (!isProduction) console.error("searchUserMessages error:", error);
    if (isProduction) {
      bugsnag_log(error, { context: "searchUserMessages (fetch failed)" });
    }
    return { error: defaultServerErrorMessage };
  }
};

const markChatThreadRead = async (threadUuid) => {
  if (!threadUuid) return null;
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `
        mutation MarkChatThreadRead($threadUuid: String!) {
          markChatThreadRead(threadUuid: $threadUuid) {
            threadUuid
            lastReadAt
            lastBotMessageAt
            hasUnread
          }
        }`,
      variables: { threadUuid },
    }),
  };

  try {
    const res = await fetch("/graphql", requestOptions);
    const json = await res.json();

    if (json.errors?.length) {
      const error = json.errors[0];
      if (error.code === "AUTH") return { error: "Session Expired" };
      if (isProduction) {
        bugsnag_log(error, {
          context: "markChatThreadRead",
          backendStack: error.extensions?.exception?.stacktrace,
        });
        return { error: defaultServerErrorMessage };
      }
      return { error: error.message };
    }

    return json.data?.markChatThreadRead ?? null;
  } catch (e) {
    console.error("markChatThreadRead error", e);
    if (isProduction) {
      bugsnag_log(e, { context: "markChatThreadRead (fetch failed)" });
    }
    return { error: defaultServerErrorMessage };
  }
};

const getTickerChartData = async (
  tickerId,
  message,
  secondarySeriesKey = null,
) => {
  const body = JSON.stringify({
    query: `
      query TickerChartData($tickerId: ID!, $messageText: String, $secondarySeriesKey: String) {
        tickerChartData(tickerId: $tickerId, messageText: $messageText, secondarySeriesKey: $secondarySeriesKey) {
          id
          primaryLabel
          label1
          label2
          label3
          values1 { x y }
          values2 { x y }
          values3 { x y }
          availableSecondaryKeys
          currentPrice
          currentPriceUpdatedAt
          currentPriceUpdatedAtIso
          closePrice
          priceChange1d
          pricePctChange1d
          pricePctChangeAh
          pricePctChange24hr
          assetType
          logoUrls
        }
      }
    `,
    variables: { tickerId, messageText: message, secondarySeriesKey },
  });

  try {
    const res = await fetch("/graphql", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
    });

    const json = await res.json();
    if (json.errors?.length) throw new Error(json.errors[0].message);

    return json.data.tickerChartData;
  } catch (err) {
    if (!isProduction) console.error("getTickerChartData error:", err);
    bugsnag_log(err);
    throw err;
  }
};

const regenerateQuestionRecommendations = async (
  userData,
  appProps,
  question,
) => {
  // NOTE. If we ever want to allow users to make requests without account, will need to add back UUID paramater.
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `mutation {
        regenerateQuestionRecommendations(question: "${question}") {
          messages
          tickers
          isAdvanced
          advancedType
        }
      }`,
    }),
  };
  try {
    const response = await fetch("/graphql", requestOptions);
    const data = await response.json();
    return data.data.regenerateQuestionRecommendations;
  } catch (e) {
    console.log("api errors:", e);
    return e;
  }
};

// Not in use yet..
const userNotifications = async () => {
  const requestOptions2 = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `{
        getUserNotifications {
          id
          text
          createdAt
          updatedAt
        }
      }`,
    }),
  };
  let final = fetch("/graphql", requestOptions2)
    .then((response) => {
      return response.json();
    })
    .then((data) => {
      if (data != "success") {
        return data.data;
      }
    })
    .catch((error) => {
      console.log("api errors:", error);
    });

  return final;
};

const EXT_TO_MIME = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  gif: "image/gif",
  heic: "image/heic",
  heif: "image/heif",
  webm: "audio/webm",
  ogg: "audio/ogg",
  mp3: "audio/mpeg",
  m4a: "audio/mp4",
  wav: "audio/wav",
  txt: "text/plain",
  text: "text/plain",
  csv: "text/csv",
  xls: "application/vnd.ms-excel",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

const guessMimeFromName = (name = "") => {
  const ext = name.split(".").pop()?.toLowerCase();
  return ext ? EXT_TO_MIME[ext] : undefined;
};

const normalizedUploadMime = (file, explicitMime = "") => {
  const rawMime =
    explicitMime ||
    file?.type ||
    guessMimeFromName(file?.name || "") ||
    "application/octet-stream";
  return rawMime.toString().split(";")[0].trim().toLowerCase();
};

export const prepareImageUploads = async (filesMeta, options = {}) => {
  const { signal } = options || {};
  if (!filesMeta?.length) return [];
  const body = {
    query: `mutation PrepareImageUploads($files: [UploadRequestInput!]!) {
      prepareImageUploads(files: $files) {
        storageKey
        uploadUrl
        headers { name value }
      }
    }`,
    variables: {
      files: filesMeta.map((m) => ({
        filename: m.file.name,
        mime: normalizedUploadMime(m.file, m.mime),
        bytes: m.file.size,
      })),
    },
  };
  const r = await fetch("/graphql", {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Tab-Id": getTabId() },
    body: JSON.stringify(body),
    signal,
  });
  const data = await r.json();
  if (data.errors?.length)
    throw new Error(data.errors[0]?.message || "prepareImageUploads failed");
  return data.data.prepareImageUploads; // array aligned with filesMeta
};

export const uploadBrowserDirect = async (target, file, options = {}) => {
  const { signal } = options || {};
  const hdrs = {};
  (target.headers || []).forEach((h) => {
    hdrs[h.name] = h.value;
  });
  // Most S3 presigns work with PUT and these headers:
  if (!hdrs["Content-Type"])
    hdrs["Content-Type"] = file.type || "application/octet-stream";
  const resp = await fetch(target.uploadUrl, {
    method: "PUT",
    headers: hdrs,
    body: file,
    signal,
  });
  if (!resp.ok) throw new Error(`Upload failed: ${resp.status}`);
};

const buildImagesPayload = (filesMeta, targets) => {
  // Merge selecting stable metadata the backend will persist
  return filesMeta.map((m, i) => ({
    storageKey: targets[i].storageKey,
    filename: m.file.name,
    mime: normalizedUploadMime(m.file, m.mime),
    bytes: m.file.size,
    width: m.width,
    height: m.height,
    sha256: m.sha256,
  }));
};

const sendChatMessages = async (
  message,
  isExplainWhy,
  tickerIds = null,
  deviceType,
  platformType = "browser",
  threadUuid = undefined,
  onThreadIdResolved = () => {},
  imagesMeta = [],
  editAnchorMessageId = null,
  tradeProposalId = null,
  options = {},
) => {
  const {
    signal,
    adminBaseDirectiveModelName = null,
    adminBaseDirectiveReasoningEffort = null,
    responseLength = null,
    tickerPreplanId = null,
  } = options || {};
  const ENABLE_VOICE_CONTROLS = false; // temporary kill switch
  let attachments = [];
  if (imagesMeta.length) {
    try {
      const targets = await prepareImageUploads(imagesMeta, { signal }); // ok to keep name for now
      console.log("targets?", targets);
      await Promise.all(
        targets.map((t, i) => uploadBrowserDirect(t, imagesMeta[i].file, { signal })),
      );
      attachments = buildAttachmentsPayload(imagesMeta, targets);
    } catch (error) {
      if (error?.name === "AbortError") {
        return { aborted: true, skipBotMessage: true };
      }

      if (isProduction) {
        bugsnag_log(error, {
          context: "userSendMessage (Attachment upload failed)",
          graphqlVars: {
            message,
            threadUuid,
            editAnchorMessageId,
            tradeProposalId,
            deviceType,
            platformType,
            attachmentsCount: imagesMeta.length,
          },
        });
      }

      return retryableChatSendFailure({
        fetchErrorName: error?.name || null,
        fetchErrorMessage: error?.message || null,
      });
    }
  }

  const variables = {
    message,
    isExplainWhy,
    tickerIds,
    tradeProposalId,
    deviceType,
    platformType,
    conversationSurface: "classic_chat",
    attachments,
    speakEnabled:
      ENABLE_VOICE_CONTROLS &&
      typeof window !== "undefined" &&
      window.localStorage.getItem("incite_speak_enabled") === "1",
    responseLength,
    tickerPreplanId,
  };
  if (adminBaseDirectiveModelName) {
    variables.adminBaseDirectiveModelName = adminBaseDirectiveModelName;
  }
  if (adminBaseDirectiveReasoningEffort) {
    variables.adminBaseDirectiveReasoningEffort = adminBaseDirectiveReasoningEffort;
  }
  if (threadUuid) variables.threadUuid = threadUuid;
  if (editAnchorMessageId != null && editAnchorMessageId != undefined) variables.editAnchorMessageId = editAnchorMessageId;
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      // NOTE: Will need to pass this to any request where the backend will be doing a websocket broadcast
      "X-Tab-Id": getTabId(),
    },
    body: JSON.stringify({
      query: `mutation SendMessage($message: String!, $isExplainWhy: Boolean!, $tickerIds: [ID], $tradeProposalId: ID, $deviceType: String,
        $platformType: String, $conversationSurface: String, $threadUuid: String, $attachments: [AttachmentRefInput!], $editAnchorMessageId: ID, $speakEnabled: Boolean, $adminBaseDirectiveModelName: String, $adminBaseDirectiveReasoningEffort: String, $responseLength: String, $tickerPreplanId: ID) {
        userSendMessage(message: $message, isExplainWhy: $isExplainWhy, tickerIds: $tickerIds, tradeProposalId: $tradeProposalId, deviceType: $deviceType,
          platformType: $platformType, conversationSurface: $conversationSurface, threadUuid: $threadUuid, attachments: $attachments, editAnchorMessageId: $editAnchorMessageId, speakEnabled: $speakEnabled, adminBaseDirectiveModelName: $adminBaseDirectiveModelName, adminBaseDirectiveReasoningEffort: $adminBaseDirectiveReasoningEffort, responseLength: $responseLength, tickerPreplanId: $tickerPreplanId) {
          botMessage {
            id
            sender
            senderUsername
            message
            advancedFilteringMessage
            createdAt
            tickerDescription
            answeredTickerIds
            answeredTickerMap {
              id
              ticker
            }
            ticker
            currentPrice
            currentPriceUpdatedAt
            currentPriceUpdatedAtIso
            closePrice
            priceChange1d
            pricePctChange1d
            pricePctChangeAh
            pricePctChange24hr
            assetType
            logoUrls
            label1
            label2
            label3
            dates
            values1 {
              x
              y
            }
            values2 {
              x
              y
            }
            values3 {
              x
              y
            }
            searchUsed
            searchSources {
              title
              url
            }
            followupButtonQuestions
            tierRecommendation
            primaryLabel
            availableSecondaryKeys
            tradingScale
            refinedQueryObj
            sportsCardData
            agenticChartState
            marketAnalysisArtifacts
            responseExtractedChartEntities
            workspaceTurnContext
            tickerPrices {
              date
              open
              high
              low
              close
              volume
            }
            attachments {
              id
              kind
              filename
              mime
              bytes
              width
              height
              url
              thumbUrl
              createdAt
            }
          }
          questionsPrepop
          questionsPrepopTickers
          isAdvanced
          advancedType
          showSaveHomePopup
          showUserProfession
          userProfession
          showAskShareMessage
          showSignUp
          showSubscriptionOffer
          shouldShowReview
          shouldShowReferralPrompt
          referralPromptKind
          referralProgram {
            eligible
            status
            shareUrl
            linkCode
            qualifiedCount
            pendingCount
            totalCount
            threshold
            remainingCount
            inProgress
            rewardAvailable
            rewardStatus
            rewardLabel
            rewardPlanTier
            rewardValueCents
            rewardDisplayLabel
            rewardProvider
            rewardRedemptionKind
            rewardOfferDescription
            rewardOfferIdentifier
            shouldShowPrompt
            promptKind
            terms
            referrals {
              status
              maskedEmail
              emailConfirmedAt
              firstQuestionAt
              qualifiedAt
              createdAt
            }
            nextPromptEligibleAt
          }
          shouldShowServiceTipPrompt
          serviceTipPrompt {
            id
            headline
            body
            serviceLabel
            currency
            amountOptionsMinorUnits
            amountOptionLabels
            recommendedAmountMinorUnits
            recommendedAmountLabel
            experienceCategory
            supporterLevel
          }
          threadTitle
          threadUuid
          freeUnlimitedOnly
          hasShownFreeUnlimitedMessage
          conversionGate
          skipBotMessage
          recommendedActions
          showTextSetupModal
          textSetupContext
        }
      }`,
      variables: variables,
    }),
    signal,
  };

  let response;
  let clone;
  let requestUrl = graphqlEndpoint();
  try {
    response = await fetch(requestUrl, requestOptions);
  
    clone = response.clone(); // just so we can log when response.json() fails
    const contentType = response.headers?.get?.("content-type") || "";
    if (!contentType.includes("json")) {
      const bodySnippet = (await clone.text()).slice(0, 800);
      const retryableStatus = RETRYABLE_CHAT_HTTP_STATUSES.has(response.status);

      if (isProduction) {
        bugsnag_log(new Error(`GraphQL returned non-JSON response (${response.status})`), {
          context: "userSendMessage (Non-JSON GraphQL response)",
          graphqlVars: {
            message: message,
            threadUuid,
            editAnchorMessageId,
            tradeProposalId,
            deviceType,
            platformType,
            attachmentsCount: attachments.length,
            requestUrl,
            response: responseDebugMetadata(response),
            bodySnippet,
          },
        });
      }

      if (retryableStatus) {
        return retryableChatSendFailure({
          httpStatus: response.status,
          response: responseDebugMetadata(response),
        });
      }

      return { error: defaultServerErrorMessage };
    }

    const data = await response.json();

    if (data.errors && data.errors.length > 0) {
      const error = data.errors[0];
      const errorCode = graphqlErrorCode(error);
      if (errorCode === "AUTH") {
        return { error: "Session Expired" }; //Your session has expired, please log in." };
      } else if (errorCode === "THREAD_NOT_FOUND") {
        return {
          error: error.message || "This chat thread is no longer available. Please start a new chat.",
          threadNotFound: true,
          threadUuid,
        };
      } else if (errorCode === "LIMIT-SAME-QUESTION") {
        return {
          error: error.message,
          show_share: false,
        };
      } else if (errorCode === "LIMIT-SHARE") {
        return {
          error: error.message,
          show_share: true,
        };
      } else if (errorCode === "LIMIT") {
        return {
          error: error.message,
        };
      } else if (errorCode === "LIMIT-TEMP-BAN") {
        return {
          error: error.message,
        };
      } else if (errorCode === "LIMIT-PERM-BAN") {
        return {
          error: error.message,
        };
      } else if (errorCode === "LIMIT-PAYWALL") {
        const conversionGate = conversionGateFromGraphqlError(error);
        return {
          error: error.message,
          show_paywall: true,
          conversion_gate: conversionGate,
          conversionGate,
        };
      } else if (errorCode === "LIMIT-PAST-DUE") {
        return {
          error: error.message,
          // show_paywall: true,
          show_past_due_modal: true,
        };
      } else if (errorCode === "LIMIT-SIGN-UP") {
        const conversionGate = conversionGateFromGraphqlError(error);
        return {
          //error: '  ', // don' show anythign in response, they will have button to re-ask question
          error: error.message,
          show_sign_up: true,
          conversion_gate: conversionGate,
          conversionGate,
        };
      } else if (errorCode === "SERVER-AFTER-GENERATED") {
        if (isProduction) {
          // bugsnag_log(error, "userSendMessage (SERVER-AFTER-GENERATED)");
          bugsnag_log(error, {
            context: "userSendMessage (SERVER-AFTER-GENERATED)",
            // graphqlVars:  params,
            graphqlVars: { message: message }, // just using for message
            backendStack: error.extensions?.exception?.stacktrace,
          });
          return { error: defaultServerErrorMessage };
        } else {
          return { error: error.message };
        }
      } else {
        if (isProduction) {
          // in production, just show graceful error message
          // bugsnag_log(error, "userSendMessage");
          bugsnag_log(error, {
            context: "userSendMessage",
            // graphqlVars:  params,
            graphqlVars: { message: message }, // just using for message
            backendStack: error.extensions?.exception?.stacktrace,
          });
          return { error: defaultServerErrorMessage };
        } else {
          return { error: error.message };
        }
      }
    }

    const result = data.data?.userSendMessage;
    if (!result)
      return { error: "Unexpected server error while sending message" };

    const { threadUuid: realUuid, threadTitle } = result;

    if (
      threadUuid &&
      typeof onThreadIdResolved === "function" &&
      threadUuid.startsWith("temp-") &&
      realUuid &&
      realUuid !== threadUuid
    ) {
      onThreadIdResolved(threadUuid, realUuid, threadTitle || "Chat");
    }

  
    return data.data.userSendMessage;
  } catch (e) {
    if (e?.name === "AbortError") {
      return { aborted: true, skipBotMessage: true };
    }

    console.log("Error!", e);
    if (isProduction) {
      const ct = response?.headers?.get?.("content-type") ?? null;

      let bodySnippet = null;
      try {
        // clone is only defined if fetch succeeded; slice to keep logs light
        bodySnippet = clone ? (await clone.text()).slice(0, 800) : null;
      } catch (_) {}

      bugsnag_log(e, {
        context: "userSendMessage (Outer Catch)",
        // graphqlVars:  params,
        graphqlVars: {
          message: message,
          threadUuid,
          editAnchorMessageId,
          tradeProposalId,
          deviceType,
          platformType,
          attachmentsCount: attachments.length,
          requestUrl,
          bodySnippet: bodySnippet,
          contentType: ct,
          response: responseDebugMetadata(response),
        }, // just using for message
        backendStack: e.extensions?.exception?.stacktrace,
      });
    }

    return retryableChatSendFailure({
      fetchErrorName: e?.name || null,
      fetchErrorMessage: e?.message || null,
      response: responseDebugMetadata(response),
    });
  }
};

// Chat-workspace send path. The cockpit renders charts in dedicated widgets, but
// still needs the same chart-bearing payload as /chat so the chart dock can
// hydrate immediately when a response includes market data.
const sendWorkspaceChatMessage = async ({
  message,
  threadUuid = undefined,
  deviceType,
  platformType = "browser",
  imagesMeta = [],
  workspaceContext = undefined,
  editAnchorMessageId = null,
  speakEnabled = false,
  speakTurnId = null,
  voiceSessionUuid = null,
  voiceTurnId = null,
  agentTaskMarketDecisionId = null,
  responseLength = null,
  tickerPreplanId = null,
  signal = undefined,
} = {}) => {
  let attachments = [];
  if (Array.isArray(imagesMeta) && imagesMeta.length) {
    try {
      const targets = await prepareImageUploads(imagesMeta, { signal });
      await Promise.all(
        targets.map((target, index) =>
          uploadBrowserDirect(target, imagesMeta[index].file, { signal }),
        ),
      );
      attachments = buildAttachmentsPayload(imagesMeta, targets);
    } catch (error) {
      if (error?.name === "AbortError") {
        return { aborted: true, skipBotMessage: true };
      }
      return retryableChatSendFailure({
        fetchErrorName: error?.name || null,
        fetchErrorMessage: error?.message || null,
      });
    }
  }

  const variables = {
    message,
    isExplainWhy: false,
    deviceType,
    platformType,
    conversationSurface: "chat_workspace",
    attachments,
    speakEnabled: speakEnabled === true,
    speakTurnId: speakTurnId || null,
    voiceSessionUuid: voiceSessionUuid || null,
    voiceTurnId: voiceTurnId || null,
    responseLength,
    tickerPreplanId,
  };
  if (threadUuid) variables.threadUuid = threadUuid;
  if (workspaceContext) variables.workspaceContext = workspaceContext;
  if (agentTaskMarketDecisionId != null) {
    variables.agentTaskMarketDecisionId = agentTaskMarketDecisionId;
  }
  if (editAnchorMessageId != null) {
    variables.editAnchorMessageId = editAnchorMessageId;
  }

  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Tab-Id": getTabId(),
    },
    signal,
    body: JSON.stringify({
      query: `mutation WorkspaceSendMessage($message: String!, $isExplainWhy: Boolean!, $deviceType: String, $platformType: String, $conversationSurface: String, $threadUuid: String, $attachments: [AttachmentRefInput!], $workspaceContext: ChatWorkspaceTurnContextInput, $editAnchorMessageId: ID, $speakEnabled: Boolean, $speakTurnId: String, $voiceSessionUuid: String, $voiceTurnId: String, $agentTaskMarketDecisionId: ID, $responseLength: String, $tickerPreplanId: ID) {
        userSendMessage(message: $message, isExplainWhy: $isExplainWhy, deviceType: $deviceType, platformType: $platformType, conversationSurface: $conversationSurface, threadUuid: $threadUuid, attachments: $attachments, workspaceContext: $workspaceContext, editAnchorMessageId: $editAnchorMessageId, speakEnabled: $speakEnabled, speakTurnId: $speakTurnId, voiceSessionUuid: $voiceSessionUuid, voiceTurnId: $voiceTurnId, agentTaskMarketDecisionId: $agentTaskMarketDecisionId, responseLength: $responseLength, tickerPreplanId: $tickerPreplanId) {
          botMessage {
            id
            sender
            senderUsername
            message
            advancedFilteringMessage
            createdAt
            interactionMode
            presentationMode
            voiceTurnId
            voiceSessionUuid
            voiceSessionStatus
            voiceSessionStartedAt
            voiceSessionEndedAt
            tickerDescription
            answeredTickerIds
            answeredTickerMap {
              id
              ticker
            }
            ticker
            currentPrice
            currentPriceUpdatedAt
            currentPriceUpdatedAtIso
            closePrice
            priceChange1d
            pricePctChange1d
            pricePctChangeAh
            pricePctChange24hr
            assetType
            logoUrls
            label1
            label2
            label3
            dates
            values1 {
              x
              y
            }
            values2 {
              x
              y
            }
            values3 {
              x
              y
            }
            searchUsed
            searchSources {
              title
              url
            }
            followupButtonQuestions
            tierRecommendation
            primaryLabel
            availableSecondaryKeys
            tradingScale
            refinedQueryObj
            sportsCardData
            agenticChartState
            marketAnalysisArtifacts
            responseExtractedChartEntities
            workspaceTurnContext
            tickerPrices {
              date
              open
              high
              low
              close
              volume
            }
            attachments {
              id
              kind
              filename
              mime
              bytes
              width
              height
              url
              thumbUrl
              createdAt
            }
          }
          questionsPrepop
          threadTitle
          threadUuid
          skipBotMessage
          workspaceState
          workspaceActions
          workspaceEvent
          agentTaskEvent
          recommendedActions
          showTextSetupModal
          textSetupContext
        }
      }`,
      variables,
    }),
  };

  let response;
  try {
    response = await fetch(graphqlEndpoint(), requestOptions);
    const contentType = response.headers?.get?.("content-type") || "";
    if (!contentType.includes("json")) {
      if (RETRYABLE_CHAT_HTTP_STATUSES.has(response.status)) {
        return retryableChatSendFailure({ httpStatus: response.status });
      }
      return { error: defaultServerErrorMessage };
    }

    const data = await response.json();
    if (data.errors && data.errors.length > 0) {
      const error = data.errors[0];
      const errorCode = graphqlErrorCode(error);
      if (errorCode === "AUTH") return { error: "Session Expired" };
      if (errorCode === "THREAD_NOT_FOUND") {
        return {
          error:
            error.message ||
            "This chat thread is no longer available. Please start a new chat.",
          threadNotFound: true,
          threadUuid,
        };
      }
      if (errorCode === "LIMIT-SIGN-UP") {
        const conversionGate = conversionGateFromGraphqlError(error);
        return {
          error: error.message,
          show_sign_up: true,
          conversion_gate: conversionGate,
          conversionGate,
        };
      }
      return { error: error.message || defaultServerErrorMessage };
    }

    const result = data.data?.userSendMessage;
    if (!result) return { error: "Unexpected server error while sending message" };
    return result;
  } catch (error) {
    if (error?.name === "AbortError") {
      return { aborted: true, skipBotMessage: true };
    }
    return retryableChatSendFailure({
      fetchErrorName: error?.name || null,
      fetchErrorMessage: error?.message || null,
    });
  }
};

const cancelChatMessages = async (threadUuid) => {
  const variables = { threadUuid };
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Tab-Id": getTabId(),
    },
    body: JSON.stringify({
      query: `
        mutation CancelMessage($threadUuid: String) {
          cancelUserMessage(threadUuid: $threadUuid)
        }
      `,
      variables,
    }),
  };

  try {
    const res = await fetch("/graphql", requestOptions);
    const data = await res.json();
    return data.data?.cancelUserMessage === true;
  } catch (e) {
    console.error("Error cancelling message", e);
    return false;
  }
};

const bootstrapReturningUserThread = async () => {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Tab-Id": getTabId(),
    },
    body: JSON.stringify({
      query: `
        mutation BootstrapReturningUserThread {
          bootstrapReturningUserThread {
            triggered
            threadUuid
            threadTitle
            reason
          }
        }
      `,
    }),
  };

  try {
    const res = await fetch("/graphql", requestOptions);
    const data = await res.json();
    if (data.errors?.length) {
      return {
        triggered: false,
        threadUuid: null,
        threadTitle: null,
        reason: data.errors[0]?.message || "graphql_error",
      };
    }
    return (
      data?.data?.bootstrapReturningUserThread || {
        triggered: false,
        threadUuid: null,
        threadTitle: null,
        reason: "empty_response",
      }
    );
  } catch (e) {
    return {
      triggered: false,
      threadUuid: null,
      threadTitle: null,
      reason: "network_error",
    };
  }
};

const bootstrapFirstUserExperience = async (threadUuid, { signal } = {}) => {
  const variables = { threadUuid };
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Tab-Id": getTabId(),
    },
    body: JSON.stringify({
      query: `
        mutation BootstrapFirstUserExperience($threadUuid: String) {
          bootstrapFirstUserExperience(threadUuid: $threadUuid) {
            botMessage {
              id
              sender
              senderUsername
              message
              advancedFilteringMessage
              createdAt
              tickerDescription
              answeredTickerIds
              answeredTickerMap {
                id
                ticker
              }
              ticker
              currentPrice
              currentPriceUpdatedAt
              currentPriceUpdatedAtIso
              closePrice
              priceChange1d
              pricePctChange1d
              pricePctChangeAh
              pricePctChange24hr
              assetType
              deviceType
              logoUrls
              label1
              label2
              label3
              dates
              values1 { x y }
              values2 { x y }
              values3 { x y }
              tickerPrices {
                date
                open
                high
                low
                close
                volume
              }
              primaryLabel
              availableSecondaryKeys
              refinedQueryObj
              sportsCardData
              agenticChartState
              marketAnalysisArtifacts
              tradingScale
              searchUsed
              searchSources {
                title
                url
              }
              followupButtonQuestions
              tierRecommendation
              attachments {
                id
                kind
                filename
                mime
                bytes
                width
                height
                url
                thumbUrl
                createdAt
              }
            }
            questionsPrepop
            questionsPrepopTickers
            threadTitle
            threadUuid
            skipBotMessage
          }
        }
      `,
      variables,
    }),
    signal,
  };

  try {
    const res = await fetch("/graphql", requestOptions);
    const data = await res.json();
    if (data.errors?.length) {
      return {
        botMessage: null,
        threadTitle: null,
        threadUuid: null,
        skipBotMessage: true,
        error: data.errors[0]?.message || defaultServerErrorMessage,
      };
    }
    return (
      data?.data?.bootstrapFirstUserExperience || {
        botMessage: null,
        threadTitle: null,
        threadUuid: null,
        skipBotMessage: true,
      }
    );
  } catch (e) {
    return {
      botMessage: null,
      threadTitle: null,
      threadUuid: null,
      skipBotMessage: true,
      error:
        e?.name === "AbortError"
          ? "Generation stopped."
          : defaultServerErrorMessage,
    };
  }
};

const updateUserReview = async (hasRated, rating) => {
  const variables = { hasRated, rating };
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      query: `
        mutation UpdateUserReview($rating: Int, $hasRated: Boolean!) {
          updateUserReview(rating: $rating, hasRated: $hasRated) {
            ratings
            hasRated
          }
        }`,
      variables,
    }),
  };

  try {
    const res = await fetch("/graphql", requestOptions);
    const data = await res.json();
    return data.data?.updateUserReview;
  } catch (e) {
    console.error("Error sending review", e);
  }
};

const recordServiceTipPromptShown = async (promptId) => {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `
        mutation RecordServiceTipPromptShown($promptId: ID!) {
          recordServiceTipPromptShown(promptId: $promptId) {
            id
          }
        }`,
      variables: { promptId },
    }),
  };

  const res = await fetch("/graphql", requestOptions);
  const data = await res.json();
  return data.data?.recordServiceTipPromptShown;
};

const dismissServiceTipPrompt = async (promptId) => {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `
        mutation DismissServiceTipPrompt($promptId: ID!) {
          dismissServiceTipPrompt(promptId: $promptId) {
            id
          }
        }`,
      variables: { promptId },
    }),
  };

  const res = await fetch("/graphql", requestOptions);
  const data = await res.json();
  return data.data?.dismissServiceTipPrompt;
};

const createServiceTipCheckoutSession = async (promptId, amountMinorUnits) => {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `
        mutation CreateServiceTipCheckoutSession($promptId: ID!, $amountMinorUnits: Int!) {
          createServiceTipCheckoutSession(promptId: $promptId, amountMinorUnits: $amountMinorUnits) {
            status
            checkoutUrl
            error
          }
        }`,
      variables: { promptId, amountMinorUnits },
    }),
  };

  const res = await fetch("/graphql", requestOptions);
  const data = await res.json();
  return data.data?.createServiceTipCheckoutSession || { status: "error", error: "Checkout failed" };
};

const getServiceTipPaymentMethodStatus = async (promptId) => {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `
        mutation GetServiceTipPaymentMethodStatus($promptId: ID!) {
          getServiceTipPaymentMethodStatus(promptId: $promptId) {
            available
            label
            brand
            last4
            error
          }
        }`,
      variables: { promptId },
    }),
  };

  const res = await fetch("/graphql", requestOptions);
  const data = await res.json();
  if (data.errors?.length) {
    return { available: false, error: data.errors[0].message };
  }

  return data.data?.getServiceTipPaymentMethodStatus || { available: false };
};

const payServiceTipWithSavedPaymentMethod = async (promptId, amountMinorUnits) => {
  const requestOptions = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `
        mutation PayServiceTipWithSavedPaymentMethod($promptId: ID!, $amountMinorUnits: Int!) {
          payServiceTipWithSavedPaymentMethod(promptId: $promptId, amountMinorUnits: $amountMinorUnits) {
            status
            checkoutUrl
            error
            paymentStatus
            declineCode
            savedPaymentMethodLabel
            prompt {
              id
              supporterLevel
            }
          }
        }`,
      variables: { promptId, amountMinorUnits },
    }),
  };

  const res = await fetch("/graphql", requestOptions);
  const data = await res.json();
  if (data.errors?.length) {
    return { status: "error", error: data.errors[0].message };
  }

  return data.data?.payServiceTipWithSavedPaymentMethod || { status: "error", error: "Tip payment failed" };
};

export {
  sendChatMessages,
  sendWorkspaceChatMessage,
  adminCreateTargetedThread,
  getAdminChatModelCatalog,
  regenerateQuestionRecommendations,
  getAllUserThreads,
  renameThread,
  deleteChatThread,
  getThreadMessages,
  getUserThreadsPage,
  searchUserMessages,
  markChatThreadRead,
  getTickerChartData,
  cancelChatMessages,
  bootstrapFirstUserExperience,
  bootstrapReturningUserThread,
  updateUserReview,
  recordServiceTipPromptShown,
  dismissServiceTipPrompt,
  createServiceTipCheckoutSession,
  getServiceTipPaymentMethodStatus,
  payServiceTipWithSavedPaymentMethod,
};
